/**
 * ManagerAdequacyQueue.tsx
 *
 * Manager view for CP10 PUSH defense adequacy queue.
 *   - Lists pending submissions for the manager's tenant.
 *   - Shows the rep's full free-text PUSH answers and triggering SOFT_STOPs.
 *   - Approve (Adequate) / Reject (Inadequate) with required free-text reason
 *     when rejecting.
 *   - On reject, BUILD-RETRY-CRON opens a 48-hr retry window (server-side).
 *
 * Renders inside AdminHome (Manager / Executive). Read-only for other roles.
 */

import React, { useEffect, useState } from "react";
import { api } from "@/lib/session";

type QueueItem = {
  id: string;
  tenant_id: number;
  deal_id: string;
  rep_user_id: string;
  push1_new_fact: string;
  push2_dated_milestone: string;
  push3_resolution_plan: string;
  push4_customer_comms: "yes" | "no";
  triggering_soft_stop_ids: string;
  is_retry: boolean;
  status: "pending" | "adequate" | "inadequate" | "auto_loss";
  retry_deadline: string | null;
  submitted_at: string;
};

const BLACK = "#0B0B0F";
const RED = "#DC2626";

export const ManagerAdequacyQueue: React.FC = () => {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api("/api/cp10/push-defense/queue?status=pending");
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setItems(j.items ?? []);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function decide(
    item: QueueItem,
    decision: "adequate" | "inadequate",
  ) {
    if (decision === "inadequate" && rejectNote.trim().length === 0) {
      setError("A reason is required when rejecting an adequacy submission.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await api(
        `/api/cp10/push-defense/queue/${item.id}/decide`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            decision,
            manager_note: decision === "inadequate" ? rejectNote.trim() : null,
          }),
        },
      );
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.error ?? `HTTP ${r.status}`);
      }
      setOpenId(null);
      setRejectNote("");
      await load();
    } catch (e: any) {
      setError(e?.message ?? "Failed to record decision");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="p-4 text-sm text-gray-500">
        Loading PUSH defense queue…
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            PUSH Defense Adequacy Queue
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Review rep submissions for deals attempting to PUSH to next quarter.
          </p>
        </div>
        <button
          onClick={load}
          className="text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-900"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div
          data-testid="adequacy-queue-error"
          className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
          No pending PUSH defense items.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => {
            const isOpen = openId === it.id;
            return (
              <li
                key={it.id}
                className="rounded border border-gray-200 bg-gray-50 p-4"
                data-testid={`adequacy-queue-item-${it.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900">
                      Deal {it.deal_id}
                      {it.is_retry && (
                        <span
                          className="ml-2 inline-block rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium uppercase text-yellow-800"
                          style={{ letterSpacing: "0.05em" }}
                        >
                          Retry
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      Submitted {new Date(it.submitted_at).toLocaleString()}
                      {it.retry_deadline && (
                        <>
                          {" · Deadline "}
                          {new Date(it.retry_deadline).toLocaleString()}
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setOpenId(isOpen ? null : it.id)}
                    className="text-xs font-semibold uppercase tracking-wider text-gray-700 underline"
                  >
                    {isOpen ? "Hide" : "Read submission"}
                  </button>
                </div>

                {isOpen && (
                  <div className="mt-4 space-y-3 border-t border-gray-200 pt-4">
                    <Field
                      label="Q10.PUSH.1 — Materially new fact"
                      value={it.push1_new_fact}
                    />
                    <Field
                      label="Q10.PUSH.2 — Dated milestone (must be within 90 days)"
                      value={it.push2_dated_milestone}
                    />
                    <Field
                      label="Q10.PUSH.3 — Resolution plan"
                      value={it.push3_resolution_plan}
                    />
                    <Field
                      label="Q10.PUSH.4 — Customer comms supporting next-quarter close"
                      value={it.push4_customer_comms.toUpperCase()}
                    />
                    <Field
                      label="Triggering SOFT_STOPs"
                      value={it.triggering_soft_stop_ids || "(none recorded)"}
                    />

                    <div className="pt-2">
                      <label className="block text-xs font-medium uppercase tracking-wider text-gray-600">
                        Reason (required to reject)
                      </label>
                      <textarea
                        value={openId === it.id ? rejectNote : ""}
                        onChange={(e) => setRejectNote(e.target.value)}
                        disabled={submitting}
                        className="mt-1 w-full rounded border border-gray-300 p-2 text-sm"
                        rows={3}
                        placeholder="Required when rejecting. E.g. 'Date in Q10.PUSH.2 references prior quarter close — not within next 90 days.'"
                      />
                    </div>

                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => decide(it, "inadequate")}
                        disabled={submitting}
                        data-testid={`reject-${it.id}`}
                        style={{
                          background: RED,
                          color: "white",
                          padding: "6px 14px",
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          border: "none",
                          cursor: submitting ? "not-allowed" : "pointer",
                          opacity: submitting ? 0.5 : 1,
                        }}
                      >
                        Reject — Inadequate
                      </button>
                      <button
                        type="button"
                        onClick={() => decide(it, "adequate")}
                        disabled={submitting}
                        data-testid={`approve-${it.id}`}
                        style={{
                          background: BLACK,
                          color: "white",
                          padding: "6px 14px",
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          border: "none",
                          cursor: submitting ? "not-allowed" : "pointer",
                          opacity: submitting ? 0.5 : 1,
                        }}
                      >
                        Approve — Adequate
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        className="text-xs font-medium uppercase tracking-wider text-gray-500"
        style={{ letterSpacing: "0.06em" }}
      >
        {label}
      </div>
      <div className="mt-0.5 whitespace-pre-wrap text-sm text-gray-900">
        {value}
      </div>
    </div>
  );
}

export default ManagerAdequacyQueue;
